import { NIVEIS_ACESSO, SIM_NAO, SITUACOES_ATUALIZACAO, TIPOS_FONTE } from './enums.js';

/**
 * Definição declarativa do formulário (etapas, campos, rótulos e opções).
 *
 * O front renderiza a partir daqui; o schema Zod (`schema.ts`) valida os mesmos
 * caminhos. Um teste garante que os dois continuam em sincronia.
 */

/** Versão do formulário gravada junto com cada envio. */
export const FORM_VERSION = 'v1';

interface BaseField {
  /** Chave do campo no payload (ex.: `nome` → `pasta.nome`). */
  id: string;
  label: string;
  hint?: string;
  required?: boolean;
}

export interface InputField extends BaseField {
  type: 'text' | 'email' | 'tel' | 'textarea';
}

export interface ChoiceField extends BaseField {
  type: 'radio' | 'check';
  options: readonly string[];
}

/** Anexos: não fazem parte do payload JSON, são enviados como arquivos (multipart). */
export interface FileField extends BaseField {
  type: 'file';
}

/** Lista de fichas repetíveis (ex.: várias fontes de conhecimento). */
export interface RepeatField extends BaseField {
  type: 'repeat';
  min?: number;
  fields: readonly (InputField | ChoiceField)[];
}

export type FormField = InputField | ChoiceField | FileField | RepeatField;

export interface FormStep {
  id: 'pasta' | 'fontes' | 'focal';
  /** Número exibido no cabeçalho da etapa. */
  number: string;
  title: string;
  description: string;
  fields: readonly FormField[];
}

export const FORM_STEPS = [
  {
    id: 'pasta',
    number: '01',
    title: 'Secretaria',
    description: 'Identificação da Pasta que está respondendo.',
    fields: [
      { id: 'nome', label: 'Secretaria / Órgão', type: 'text', required: true },
      { id: 'sigla', label: 'Sigla', type: 'text' },
      { id: 'resp', label: 'Quem está preenchendo (nome e cargo)', type: 'text', required: true },
      {
        id: 'email',
        label: 'E-mail institucional de quem preenche',
        type: 'email',
        required: true,
      },
    ],
  },
  {
    id: 'fontes',
    number: '02',
    title: 'Fontes de conhecimento',
    description:
      'Onde está a informação oficial da sua Pasta que a IA poderá usar para orientar o cidadão. Uma ficha por fonte — adicione quantas precisar.',
    fields: [
      {
        id: 'lista',
        label: 'Fonte',
        type: 'repeat',
        min: 1,
        fields: [
          {
            id: 'nome',
            label: 'Nome da fonte',
            hint: 'Ex.: Carta de Serviços · Manual de poda · Portal de licenciamento.',
            type: 'text',
            required: true,
          },
          { id: 'tipo', label: 'Tipo', type: 'radio', options: TIPOS_FONTE, required: true },
          {
            id: 'onde',
            label: 'Onde está',
            hint: 'Link, caminho de rede ou nome do sistema. Arquivos podem ser anexados abaixo.',
            type: 'text',
            required: true,
          },
          {
            id: 'assuntos',
            label: 'Assuntos ou serviços que essa fonte cobre',
            type: 'textarea',
            required: true,
          },
          {
            id: 'atual',
            label: 'Está atualizada?',
            type: 'radio',
            options: SITUACOES_ATUALIZACAO,
            required: true,
          },
          { id: 'acesso', label: 'Acesso', type: 'radio', options: NIVEIS_ACESSO },
          { id: 'dono', label: 'Área ou pessoa responsável pelo conteúdo', type: 'text' },
        ],
      },
      {
        id: 'arquivos',
        label: 'Anexos',
        hint: 'Documentos, manuais ou planilhas que não estão em link.',
        type: 'file',
      },
      {
        id: 'obs',
        label: 'Observações sobre as fontes',
        hint: 'Conteúdo desatualizado, divergente, ou que ainda não existe por escrito.',
        type: 'textarea',
      },
    ],
  },
  {
    id: 'focal',
    number: '03',
    title: 'Ponto Focal de IA',
    description:
      'Titular e suplente que representarão a Pasta junto ao projeto e responderão pela atualização contínua da Base.',
    fields: [
      { id: 't_nome', label: 'Titular · Nome completo', type: 'text', required: true },
      { id: 't_cargo', label: 'Titular · Cargo / função', type: 'text', required: true },
      { id: 't_email', label: 'Titular · E-mail institucional', type: 'email', required: true },
      { id: 't_zap', label: 'Titular · WhatsApp', type: 'tel', required: true },
      { id: 't_efetivo', label: 'Titular · Servidor efetivo?', type: 'radio', options: SIM_NAO },
      { id: 's_nome', label: 'Suplente · Nome completo', type: 'text', required: true },
      { id: 's_cargo', label: 'Suplente · Cargo / função', type: 'text', required: true },
      { id: 's_email', label: 'Suplente · E-mail institucional', type: 'email', required: true },
      { id: 's_zap', label: 'Suplente · WhatsApp', type: 'tel', required: true },
      { id: 's_efetivo', label: 'Suplente · Servidor efetivo?', type: 'radio', options: SIM_NAO },
    ],
  },
] as const satisfies readonly FormStep[];

export type FormStepId = (typeof FORM_STEPS)[number]['id'];
